<template>
  <a-modal
    :visible="showModal"
    :closable="false"
    :title="$t('utils.filter')"
    :dialog-style="{ top: '20px' }"
    class="sales-filter"
  >
    <a-form-model ref="formRef" :model="form" class="export-inventory">
      <a-form-model-item :label="$t('fulfillment.business')">
        <a-select
          v-model="form.business"
          mode="multiple"
          class="w-100"
          :placeholder="$t('order.choose_business')"
          show-search
          allow-clear
          :options="businessList"
          option-filter-prop="children"
          :filter-option="filterOption"
          @deselect="onDeselectBusiness"
          @change="onChangeValue($event, 'business')"
        />
      </a-form-model-item>
      <a-form-model-item :label="$t('dashboardContent.sales_channel')">
        <a-select
          v-model="form.salesChannel"
          mode="multiple"
          class="w-100"
          :placeholder="$t('order.choose_sales_channel')"
          show-search
          allow-clear
          option-filter-prop="children"
          :filter-option="filterOption"
          :not-found-content="isFetching ? undefined : null"
          :disabled="!form.business.length"
          @focus="onFocusList"
          @change="onChangeValue($event, 'salesChannel')"
        >
          <a-spin v-if="isFetching" slot="notFoundContent" size="small" />
          <a-select-option v-for="salesChannel in salesChannelList" :key="salesChannel.value">
            {{ salesChannel.label }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('fulfillment.channel')">
        <a-select
          v-model="form.channel"
          mode="multiple"
          class="w-100"
          :placeholder="$t('order.choose_channel')"
          show-search
          allow-clear
          option-filter-prop="children"
          :filter-option="filterOption"
          :not-found-content="isFetching ? undefined : null"
          :disabled="!form.business.length || !form.salesChannel.length"
          @focus="onFocusList"
          @change="onChangeValue($event, 'channel')"
        >
          <a-spin v-if="isFetching" slot="notFoundContent" size="small" />
          <template v-for="channel in channelList">
            <a-select-option v-if="channel.value === 'all'" :key="channel.value" :value="channel.value">
              {{ channel.label }}
            </a-select-option>
            <a-select-opt-group v-else :key="'group-' + channel.value" :label="channel.businessName">
              <a-select-option :value="channel.value">
                <div class="d-flex align-items-center justify-content-between">
                  <span class="mr-2 w-75 text-truncate">{{ channel.label }}</span>
                  <a-tag>{{ channel.channelName }}</a-tag>
                </div>
              </a-select-option>
            </a-select-opt-group>
          </template>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('fulfillment.warehouse')">
        <a-select
          v-model="form.warehouse"
          mode="multiple"
          class="w-100"
          :placeholder="$t('order.choose_warehouse')"
          show-search
          allow-clear
          option-filter-prop="children"
          :filter-option="filterOption"
          :not-found-content="isFetching ? undefined : null"
          :disabled="!form.business.length"
          @focus="onFocusList"
          @change="onChangeValue($event, 'warehouse')"
        >
          <a-spin v-if="isFetching" slot="notFoundContent" size="small" />
          <a-select-option v-for="warehouse in warehouseList" :key="warehouse.value">
            {{ warehouse.label }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('order.orderType')">
        <a-select
          v-model="form.orderType"
          class="w-100"
          :placeholder="$t('order.orderType')"
          :disabled="!form.business.length"
        >
          <a-select-option v-for="orderType in orderTypeList" :key="orderType.value">
            {{ orderType.label }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('order.priorityLevel')">
        <a-select
          v-model="form.priorityLevel"
          mode="multiple"
          class="w-100"
          :placeholder="$t('order.choose_priority_level')"
          show-search
          allow-clear
          option-filter-prop="children"
          :filter-option="filterOption"
          :disabled="!form.business.length"
        >
          <a-select-option v-for="priorityLevel in optionsPriority" :key="priorityLevel.value">
            {{ priorityLevel.label }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('order.order_item_status')">
        <a-select
          v-model="form.status"
          class="w-100"
          :placeholder="$t('order.choose_order_item_status')"
          :disabled="!form.business.length"
        >
          <a-select-option v-for="status in statusList" :key="status.value">
            {{ status.label }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
      <a-form-model-item :label="$t('order.ordersPeriod')">
        <a-range-picker
          v-model="form.date"
          class="w-100"
          :disabled-date="disabledDate"
          :placeholder="[$t('utils.choose') + ' ' + $t('order.ordersPeriodFrom'), $t('utils.choose') + ' ' + $t('order.ordersPeriodUntil')]"
          :allow-clear="false"
          :get-calendar-container="() => $refs.formRef.$el"
          :show-time="{ format: 'HH:mm:ss' }"
          format="YYYY-MM-DD HH:mm:ss"
          @change="onChangeDate"
        >
          <a-icon slot="suffixIcon" type="calendar" />
        </a-range-picker>
      </a-form-model-item>
    </a-form-model>
    <template #footer>
      <div class="d-flex">
        <a-button @click="resetForm">
          {{ $t('order.cleanFilter') }}
        </a-button>
        <a-button class="ml-auto" @click="$emit('close')">
          {{ $t('utils.cancel') }}
        </a-button>
        <a-button type="primary" class="ml-2" @click="submitFilter">
          {{ $t('utils.apply') }}
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { getChannelsByBusinessList } from '@/api/business'
import { getAllWarehouseList } from '@/api/warehouse'
import isEqual from 'lodash/isEqual'
import orderBy from 'lodash/orderBy'

export default {
  name: 'ModalExportInventory',
  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    optionsPriority: {
      type: Array,
      default: () => [],
    },
    businessValue: {
      type: Array,
      default: () => [],
    },
    salesChannelValue: {
      type: Array,
      default: () => [],
    },
    channelValue: {
      type: Array,
      default: () => [],
    },
    warehouseValue: {
      type: Array,
      default: () => [],
    },
    orderTypeValue: {
      type: String,
      default: 'ALL',
    },
    priorityLevelValue: {
      type: Array,
      default: () => [],
    },
    statusValue: {
      type: String,
      default: 'all',
    },
    customerValue: {
      type: String,
      default: '',
    },
    courierValue: {
      type: String,
      default: '',
    },
    orderPeriod: {
      type: Array,
      default: () => [this.$moment().subtract(30, 'days').startOf('day').format(), this.$moment().endOf('day').format()],
    },
  },
  data() {
    return {
      isFetching: false,
      form: {
        business: [],
        salesChannel: [],
        channel: [],
        warehouse: [],
        orderType: 'REGULAR',
        priorityLevel: [],
        status: 'all',
        date: [this.$moment().subtract(30, 'days').startOf('day').format(), this.$moment().endOf('day').format()],
      },
      salesChannelList: [],
      channelList: [],
      warehouseList: [],
      statusList: [
        { value: 'all', label: this.$t('order.all_status') },
        { value: 'confirmed', label: this.$t('order.confirmed') },
        { value: 'unconfirmed', label: this.$t('order.unconfirmed') },
      ],
      orderTypeList: [
        { value: 'ALL', label: this.$t('order.all_order_type') },
        { value: 'REGULAR', label: 'Regular' },
        { value: 'SAMPLE', label: 'Sample' },
        { value: 'UNKNOWN', label: 'Unknown' },
      ],
      currentBusinessId: [],
      currentSalesChannelList: [],
      currentChannelList: [],
      currentWarehouseList: [],
      originalSalesChannelList: {},
      originalChannelList: [],
    }
  },
  computed: {
    businessId() {
      return this.$store.state.user.restriction_base === 'Warehouse'
        ? Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
        : this.$route.query.business_id
    },
    businessList() {
      let list = [{ value: 'all', label: this.$t('topBar.all_Business') }]
      if (!this.form.business.includes('all')) {
        this.$store.state.user.businessList?.forEach(business => {
          if (!!this.businessId) {
            if (this.businessId === business.business_id) {
              list.push({ value: business.business_id, label: business.business_name })
            }
          } else {
            if (business.business_id !== 'all') {
              list.push({ value: business.business_id, label: business.business_name })
            }
          }
        })
      }

      return list
    },
  },
  watch: {
    showModal(value) {
      if (value) {
        let finalDate = this.orderPeriod
        if (finalDate.includes('all')) finalDate = undefined
        this.form = {
          business: this.businessValue,
          salesChannel: this.salesChannelValue,
          channel: this.channelValue,
          warehouse: this.warehouseValue,
          orderType: this.orderTypeValue,
          priorityLevel: this.priorityLevelValue,
          status: this.statusValue,
          customer: this.customerValue,
          courier: this.courierValue,
          date: finalDate,
        }
        this.onFocusList()
      } else {
        this.resetForm()
      }
    },
  },
  methods: {
    fetchList(arr) {
      arr.forEach(async type => {
        const listName = type === 'channel' ? 'channelList' : 'warehouseList'
        const currentName = type === 'channel' ? 'currentChannelList' : 'currentWarehouseList'
        const list = this.filterBusinessList()
        const params = {
          business_id: this.getBusinessId(list),
          status: type === 'channel' ? 'CONNECTED' : undefined,
        }

        let promise
        if (type === 'warehouse') {
          promise = [
            getAllWarehouseList(params).then(allData => {
              return { data: { data: allData } }
            }),
          ]
        } else {
          promise = [getChannelsByBusinessList(params)]
        }

        await Promise.all(promise)
        .then(([{ data }]) => {
          if (type === 'channel') this.originalChannelList = data
          const response = type === 'channel' ? data : data?.data
          const label = type === 'channel' ? this.$t('fulfillment.channel') : this.$t('fulfillment.warehouse')
          const formName = type
          const all = [{ value: 'all', label: `${this.$t('topBar.all_')} ${label}` }]
          const allSalesChannel = [{ value: 'all', label: this.$t('topBar.all_SalesChannel') }]

          let defaultList = []
          let defaultSalesChannelList = []
          let originalSalesChannelList = {}
          response?.forEach(res => {
            const findBusiness = this.$store.state.user.businessList.find(business => business.business_id === (res?.business_id || res?.businessId))
            const salesChannelId = res?.sales_channel_id || res?.salesChannelId
            if (type === 'channel' && salesChannelId) {
              const findSalesChannel = defaultSalesChannelList.find(salesChannel => salesChannel.value == salesChannelId)
              const item = {
                value: res.id.toString(),
                label: res?.channel_title || res?.channelTitle,
                businessName: findBusiness?.business_name || '',
                channelId: salesChannelId,
                channelCode: res?.sales_channel_code || res?.salesChannelCode,
                channelName: res?.sales_channel_name || res?.salesChannelName,
              }
              if (!findSalesChannel?.value) {
                defaultSalesChannelList.push({ value: salesChannelId?.toString(), label: res?.sales_channel_name || res?.salesChannelName })
                originalSalesChannelList[salesChannelId] = [item]
              } else {
                originalSalesChannelList[salesChannelId].push(item)
              }
            }

            defaultList.push({
              value: res.id.toString(),
              label: type === 'channel' ? (res?.channel_title || res?.channelTitle) : res?.name,
              businessName: findBusiness?.business_name || '',
              channelId: type === 'channel' ? (res?.sales_channel_id || res?.salesChannelId) : null,
              channelCode: type === 'channel' ? (res?.sales_channel_code || res?.salesChannelCode) : null,
              channelName: type === 'channel' ? (res?.sales_channel_name || res?.salesChannelName) : null,
            })
          })

          if (type === 'channel') defaultSalesChannelList = defaultSalesChannelList.length ? orderBy(defaultSalesChannelList, ['label'], ['asc']) : defaultSalesChannelList
          defaultList = defaultList.length ? orderBy(defaultList, ['label'], ['asc']) : defaultList

          const listSalesChannel = allSalesChannel.concat(defaultSalesChannelList) 
          if (type === 'channel') {
            this.salesChannelList = !this.form.salesChannel.length || (this.form.salesChannel.length && !this.form.salesChannel.includes('all'))
              ? listSalesChannel
              : allSalesChannel
            this.currentSalesChannelList = listSalesChannel
            this.originalSalesChannelList = originalSalesChannelList
          }

          const list = all.concat(defaultList)
          this[listName] = !this.form[formName].length || (this.form[formName].length && !this.form[formName].includes('all'))
            ? list
            : all
          this[currentName] = list

          if (!!type && !!this.$route.query[formName]) {
            this.form.salesChannel = this.$route.query?.sales_channel_id
              ? this.$route.query.sales_channel_id.split(',')
              : []
            this.form[formName] = this.$route.query[formName].split(',')
          }
          if (this.form[formName].length && !this.form[formName].includes('all')) {
            this.form[formName] = this.form[formName].filter(item => {
              const itemIndex = list.findIndex(i => i.value == item)
              return itemIndex >= 0
            })
          }

          if (type === 'channel') this.$emit('onSetList', { type: 'salesChannel', list: listSalesChannel })
          this.$emit('onSetList', { type, list })
        })
        .catch(err => {
          console.error(err)
          this.salesChannelList = []
          this.currentSalesChannelList = []
          this[listName] = []
          this[currentName] = []
          if (type === 'channel') {
            this.originalSalesChannelList = {}
            this.originalChannelList = []
          }
          if (type === 'channel') this.$emit('onSetList', { type: 'salesChannel', list: [] })
          this.$emit('onSetList', { type, list: [] })
        })
        .finally(() => this.isFetching = false)
      })
    },
    onChangeValue(value, type) {
      this.form[type] = value.includes('all')
        ? ['all']
        : value

      if (type === 'business' && !value.length) {
        this.form.salesChannel = []
        this.salesChannelList = []
        this.currentSalesChannelList = []
        this.form.channel = []
        this.channelList = []
        this.currentChannelList = []
        this.form.warehouse = []
        this.warehouseList = []
        this.currentWarehouseList = []
      } else if (type === 'salesChannel') {
        let listChannel = [{ value: 'all', label: this.$t('topBar.all_Channel') }]
        if (!value.length) {
          this.form.channel = []
          this.channelList = []
        } else if (value.length && !value.includes('all')) {
          this.form.salesChannel.forEach(salesChannel => {
            listChannel = listChannel.concat(this.originalSalesChannelList[salesChannel])
          })
          this.form.channel = this.form.channel.filter(item => {
            const itemIndex = listChannel.findIndex(i => i.value === item)
            return itemIndex >= 0
          })
        }
        this.salesChannelList = value.includes('all')
          ? [{ value: 'all', label: this.$t('topBar.all_SalesChannel') }]
          : this.currentSalesChannelList
        this.channelList = value.includes('all')
          ? this.currentChannelList
          : listChannel
      } else {
        const listName = type === 'channel' ? 'channelList': 'warehouseList'
        const label = type === 'channel' ? this.$t('fulfillment.channel') : this.$t('fulfillment.warehouse')
        const currentName = type === 'channel' ? 'currentChannelList' : 'currentWarehouseList'
        if (value.includes('all')) {
          this[listName] = [{ value: 'all', label: `${this.$t('topBar.all_')} ${label}` }]
        } else if (!value.includes('all') && this[listName].length === 1) {
          this[listName] = this[currentName]
        }
      }
    },
    onDeselectBusiness(value) {
      this.form.business = this.form.business.length
        ? this.form.business.filter(business => business !== value)
        : []
      this.onFocusList()
    },
    onFocusList() {
      this.isFetching = true
      if (this.$route.query.business_id) {
        this.form.business = [this.$route.query.business_id]
      }
      
      const isSame = isEqual(this.currentBusinessId, this.form.business)
      if (!isSame) {
        this.salesChannelList = []
        this.currentSalesChannelList = []
        this.channelList = []
        this.currentChannelList = []
        this.warehouseList = []
        this.currentWarehouseList = []
        this.currentBusinessId = this.form.business

        if (this.form.business.length) {
          this.fetchList(['channel', 'warehouse'])
        } else {
          this.isFetching = false
        }
      } else {
        this.isFetching = false
      }
    },
    getBusinessId(list) {
      return this.form.business.length && this.form.business.includes('all')
        ? list.join()
        : this.form.business.length && !this.form.business.includes('all')
        ? this.form.business.join()
        : undefined
    },
    filterBusinessList() {
      let list = []
      if (this.form.business.length && this.form.business.includes('all')) {
        this.$store.state.user.businessList?.forEach(business => {
          if (!!this.businessId) {
            if (this.businessId === business.business_id) {
              list.push(business.business_id)
            }
          } else {
            if (business.business_id !== 'all') {
              list.push(business.business_id)
            }
          }
        })
      }

      return list
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0]?.text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      )
    },
    disabledDate (current) {
      return current && current < this.$moment().subtract(91, 'day')
    },
    onChangeDate(date) {
      this.form.date = [this.$moment(date[0]).startOf('day').format(), this.$moment(date[1]).endOf('day').format()]
    },
    submitFilter() {
      const { business, salesChannel, channel, warehouse, orderType, priorityLevel, status, date } = this.form
      let channelList = !channel.includes('all') ? channel : []
      if (salesChannel.length && !salesChannel.includes('all') && (!channel.length || channel.includes('all'))) {
        channelList = []
        salesChannel.forEach(salesCh => {
          if (this.originalSalesChannelList?.[salesCh]) {
            channelList = channelList.concat(this.originalSalesChannelList[salesCh].map(ch => ch.value))
          }
        })
      }
      this.$emit('submit', {
        ...this.form,
        business: !business.includes('all') ? business : [],
        sales_channel: !salesChannel.includes('all') ? salesChannel : [],
        channel: channelList,
        warehouse: !warehouse.includes('all') ? warehouse : [],
        order_type: orderType,
        priority_level: !priorityLevel.includes('all') ? priorityLevel : [],
        status,
        date,
      })
    },
    resetForm() {
      this.isFetching = false
      this.form = {
        business: [],
        salesChannel: [],
        channel: [],
        warehouse: [],
        orderType: 'ALL',
        priorityLevel: [],
        status: 'all',
        date: [this.$moment().subtract(30, 'days').startOf('day').format(), this.$moment().endOf('day').format()],
      }
      this.salesChannelList = []
      this.channelList = []
      this.warehouseList = []
      this.currentBusinessId = []
      this.currentSalesChannelList = []
      this.currentChannelList = []
      this.currentWarehouseList = []
    },
  },
}
</script>

<style lang="scss">
.sales-filter .ant-modal-body {
  padding: 12px 24px !important;
}

.export-inventory {
  .ant-form-item {
    margin-bottom: 0.5rem !important;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .ant-form-item-label {
    line-height: normal;
  }
}
</style>